/**
 * RoasMonthlySummaryWidget - Виджет ROAS по месяцам
 * =================================================
 * 
 * Сводная таблица ROAS по месячным когортам с разбивкой по платформам и источникам
 * 
 * ВАЖНО: ROAS рассчитывается на клиенте с учетом галочки "показывать чистый доход"
 * - Если галочка включена: берутся серверные NET-поля InApps (*_net: без VAT страны покупки и комиссии стора)
 * - Если галочка выключена: InApps показывается как есть (полный доход)
 * - AdMon всегда показывается без изменений
 */

import React, { useState, useMemo, useEffect } from 'react';
import { BaseWidget, useBaseWidget } from './BaseWidget';
import { useWidgetData } from '../../hooks/useWidgetData';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { MetricsService } from '../../services/metricsService';
import { useDashboard } from '../../contexts/DashboardContext';
import { Card, CardContent } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { ToggleGroup, ToggleGroupItem } from '../ui/toggle-group';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Legend, ReferenceLine } from 'recharts';
import { Tooltip } from '../charts/OrderedTooltip';
import { ResponsiveChart as ResponsiveContainer } from '../ui/responsive-chart';
import { format, parseISO, startOfMonth } from 'date-fns';
import { ru } from 'date-fns/locale';
import { ChevronDown, ChevronRight, ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { CampaignIcons } from '../ui/brand-icons';
import { roasMonthly as roasMonthlySpecs } from '@ggstat/chart-specs';
import {
  calculateRevenue,
  isDayMature,
  calculateRoas,
  pickTotalRevenue,
  pickCurrentRoas,
  getRoasDays,
  bestAvailableRoas,
  DEFAULT_SORT,
  sortRows,
  sortByName,
  nextSort,
  buildMonthCurvePoints,
  buildSourceCurvePoints,
  sumNetFields
} from './roasMonthlyModel';

const formatMonth = (dateString) => {
  try {
    // Если дата уже полная (YYYY-MM-DD), не добавляем день
    // Если только YYYY-MM, добавляем -01
    const isoDate = dateString.length === 7 ? dateString + '-01' : dateString;
    const date = parseISO(isoDate);
    const formatted = format(date, "LLLL yyyy", { locale: ru });
    // Делаем первую букву заглавной (в ru локали месяцы с маленькой)
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  } catch (error) {
    return dateString;
  }
};

// Цвета денежных колонок: InApps — «долларовый» зелёный, AdMon — тёмно-синий
const INAPPS_COLOR = 'text-emerald-600 dark:text-emerald-500';
const ADMON_COLOR = 'text-blue-800 dark:text-blue-400';

/**
 * Ячейка со значением ROAS и цветовой индикацией
 */
const RoasValueCell = ({ roas }) => {
  if (roas === null || roas === undefined) {
    return <TableCell className="text-right text-muted-foreground">—</TableCell>;
  }
  
  let colorClass = 'text-muted-foreground';
  let bgClass = '';
  
  if (roas >= 100) {
    colorClass = 'text-green-600 font-bold';
    bgClass = 'bg-green-50 dark:bg-green-950/30';
  } else if (roas >= 50) {
    colorClass = 'text-yellow-600 font-semibold';
    bgClass = 'bg-yellow-50 dark:bg-yellow-950/30';
  } else if (roas > 0) {
    // Приглушённый красный: таблица почти целиком «плохая» на ранних днях,
    // насыщенный red-600 превращал её в сплошное пятно
    colorClass = 'text-red-500/80 dark:text-red-400/80';
    bgClass = 'bg-red-50/60 dark:bg-red-950/20';
  }
  
  return (
    <TableCell className={`text-right ${colorClass} ${bgClass}`}>
      {roas.toFixed(1)}%
    </TableCell>
  );
};

/**
 * Ячейка ROAS за конкретный день
 */
const RoasCell = ({ metrics, day, applyFees }) => (
  <RoasValueCell roas={calculateRoas(metrics, day, applyFees)} />
);

/**
 * Компонент статуса месяца - определяет по лучшему доступному ROAS
 */
const MonthStatus = ({ metrics, applyFees, roasDays }) => {
  const bestRoas = bestAvailableRoas(metrics, applyFees, roasDays) ?? 0;

  if (bestRoas >= 100) {
    return <Badge variant="default" className="bg-green-600">Окупился</Badge>;
  } else if (bestRoas >= 70) {
    return <Badge variant="secondary" className="bg-yellow-500 text-black">Почти</Badge>;
  } else if (bestRoas >= 50) {
    return <Badge variant="outline" className="border-yellow-500 text-yellow-600">На пути</Badge>;
  } else if (bestRoas >= 30) {
    return <Badge variant="outline">В процессе</Badge>;
  } else if (bestRoas > 0) {
    return <Badge variant="destructive">Низкий</Badge>;
  }
  return null; // Не показываем badge если нет данных
};

const ariaSort = (sort, key) => {
  if (sort.key !== key) return 'none';
  return sort.dir === 'asc' ? 'ascending' : 'descending';
};

const SortButton = ({ sortKey, sort, onSort, children }) => {
  const isActive = sort.key === sortKey;
  const Icon = !isActive ? ArrowUpDown : sort.dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <button
      type="button"
      onClick={() => onSort(sortKey)}
      className="inline-flex items-center gap-1 cursor-pointer select-none hover:opacity-80"
      title="Сортировать"
    >
      {children}
      <Icon size={12} className={isActive ? '' : 'opacity-30'} />
    </button>
  );
};

const SortableHead = ({ sortKey, sort, onSort, className = '', children }) => (
  <TableHead className={className} aria-sort={ariaSort(sort, sortKey)}>
    <SortButton sortKey={sortKey} sort={sort} onSort={onSort}>{children}</SortButton>
  </TableHead>
);

const CHART_DAYS_STORAGE_KEY = 'roas-monthly-chart-days';

// Более ранние когорты не выводятся ни в таблице, ни в итогах, ни на графиках
const MIN_MONTH = roasMonthlySpecs.MIN_MONTH;

// Палитра линий графика: по одному цвету на каждый ROAS-день
const DAY_COLORS = roasMonthlySpecs.DAY_COLORS;

// Режим графика: 'days' — X = месяц, линия на каждый ROAS-день;
// 'curves' — X = день от установки, кривая окупаемости на каждый месяц
const CHART_MODE_STORAGE_KEY = 'roas-monthly-chart-mode';
// Месяцы, снятые с графика кривых (храним исключённые — новые месяцы появляются сами)
const CURVES_HIDDEN_MONTHS_KEY = 'roas-monthly-curves-hidden-months';
// Источники для графика кривых; null — все источники (месяц целиком)
const CURVES_SOURCES_KEY = 'roas-monthly-curves-sources';
// Разбивка кривых: false — линия на месяц, true — линия на источник
const CURVES_BY_SOURCE_KEY = 'roas-monthly-curves-by-source';

// Палитра кривых (месяцы или источники): 24 цвета, соседние контрастны —
// месяцы с разницей в год не сливаются (окно графика с 2024-11 — два года)
const CURVE_COLORS = roasMonthlySpecs.CURVE_COLORS;

/**
 * Неполный (идущий) месяц: когорта ещё набирает установки и затраты,
 * поэтому на график по месяцам не попадает
 */
const isIncompleteMonth = (monthName) => {
  try {
    const isoDate = monthName.length === 7 ? monthName + '-01' : monthName;
    return parseISO(isoDate) >= startOfMonth(new Date());
  } catch (error) {
    return false;
  }
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;
  const rows = payload.filter(entry => entry.value != null);
  if (rows.length === 0) return null;
  const costs = payload[0]?.payload?.costs;
  return (
    <div className="bg-background border rounded-lg shadow-lg p-3">
      <p className="font-medium">{formatMonth(label)}</p>
      {costs != null && (
        <p className="text-xs text-muted-foreground mb-2">Затраты: {MetricsService.formatCurrency(costs)}</p>
      )}
      <div className="space-y-1">
        {rows.map(entry => (
          <p key={entry.dataKey} className="text-sm">
            <span style={{ color: entry.color }} className="font-medium">{entry.dataKey}:</span>{' '}
            <span className={`font-bold ${entry.value >= 100 ? 'text-green-600' : 'text-amber-600'}`}>
              {entry.value.toFixed(1)}%
            </span>
          </p>
        ))}
      </div>
    </div>
  );
};

/**
 * График ROAS выбранных дней по месячным когортам за всё время
 */
function MonthlyRoasChart({ data, selectedDays, applyFees }) {
  const chartData = useMemo(() => (
    // Сервер отдаёт месяцы от новых к старым (порядок таблицы), график — слева направо по времени
    [...data]
      .filter(row => row?.name && row.name >= MIN_MONTH && !isIncompleteMonth(row.name))
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(row => {
        const { name, platforms, sources, ...metrics } = row;
        const point = { month: name, costs: metrics.costs };
        selectedDays.forEach(day => {
          point[`D${day}`] = calculateRoas(metrics, day, applyFees);
        });
        return point;
      })
  ), [data, selectedDays, applyFees]);

  if (selectedDays.length === 0) {
    return (
      <p className="text-sm text-muted-foreground px-4 py-3">
        Отметьте галочками дни ROAS в заголовке таблицы — они появятся на графике по месяцам.
      </p>
    );
  }

  if (chartData.length === 0) {
    return (
      <p className="text-sm text-muted-foreground px-4 py-3">
        Нет полных месяцев для графика.
      </p>
    );
  }

  return (
    <div className="p-4">
      <ResponsiveContainer width="100%" height={360}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
          <XAxis
            dataKey="month"
            tickFormatter={(value) => formatMonth(value)}
            className="text-xs"
            tick={{ fill: 'currentColor' }}
          />
          <YAxis
            className="text-xs"
            tick={{ fill: 'currentColor' }}
            tickFormatter={(value) => `${value}%`}
            label={{ value: 'ROAS (%)', angle: -90, position: 'insideLeft' }}
          />
          <Tooltip content={<ChartTooltip />} />
          <Legend />
          <ReferenceLine y={100} stroke="#22c55e" strokeDasharray="5 5" />
          {selectedDays.map(day => (
            <Line
              key={day}
              type="monotone"
              dataKey={`D${day}`}
              stroke={DAY_COLORS[day] || '#64748b'}
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
              connectNulls={false}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * Все источники месяца (объединение по платформам), по алфавиту
 */
const collectSources = (data) => {
  const names = new Set();
  data.forEach(row => (row.platforms || []).forEach(platform =>
    (platform.sources || []).forEach(source => { if (source?.name) names.add(source.name); })
  ));
  return [...names].sort((a, b) => a.localeCompare(b));
};

const CurvesTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;
  const rows = payload.filter(entry => entry.value != null);
  if (rows.length === 0) return null;
  return (
    <div className="bg-background border rounded-lg shadow-lg p-3">
      <p className="font-medium mb-2">День {label}</p>
      <div className="space-y-1">
        {rows.map(entry => {
          const costs = entry.payload?.[`${entry.dataKey}__costs`];
          return (
            <p key={entry.dataKey} className="text-sm">
              <span style={{ color: entry.color }} className="font-medium">{entry.name}:</span>{' '}
              <span className={`font-bold ${entry.value >= 100 ? 'text-green-600' : 'text-amber-600'}`}>
                {entry.value.toFixed(1)}%
              </span>
              {costs != null && (
                <span className="text-muted-foreground"> · {MetricsService.formatCurrency(costs)}</span>
              )}
            </p>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Кривые окупаемости: одна линия на месячную когорту, X — день от установки.
 * Все кривые стартуют с D0, поэтому месяцы сравниваются напрямую.
 */
function MonthlyCurvesChart({ data, roasDays, applyFees, hiddenMonths, onToggleMonth, onSetHiddenMonths, selectedSources, onSetSources, bySource }) {
  const months = useMemo(() => (
    [...data]
      .filter(row => row?.name && row.name >= MIN_MONTH && !isIncompleteMonth(row.name))
      .sort((a, b) => a.name.localeCompare(b.name))
  ), [data]);

  const allSources = useMemo(() => collectSources(months), [months]);
  const monthColor = useMemo(() => {
    const map = {};
    months.forEach((row, index) => { map[row.name] = CURVE_COLORS[index % CURVE_COLORS.length]; });
    return map;
  }, [months]);
  // Цвет и ключ источника — по позиции в полном списке, чтобы не прыгали при смене выбора.
  // Ключ без имени: имена источников могут содержать точки, которые recharts читает как путь.
  const { sourceColor, sourceKey } = useMemo(() => {
    const colors = {};
    const keys = {};
    allSources.forEach((source, index) => {
      colors[source] = CURVE_COLORS[index % CURVE_COLORS.length];
      keys[source] = `src_${index}`;
    });
    return { sourceColor: colors, sourceKey: keys };
  }, [allSources]);

  const visibleMonths = useMemo(
    () => months.filter(row => !hiddenMonths.includes(row.name)),
    [months, hiddenMonths]
  );

  const isAllSources = selectedSources === null;
  const sourceValue = useMemo(
    () => (isAllSources ? allSources : selectedSources.filter(s => allSources.includes(s))),
    [isAllSources, allSources, selectedSources]
  );
  // Выбраны все источники: явно (null) или полным списком
  const isEverySelected = isAllSources || sourceValue.length === allSources.length;
  const hasNoSources = !isAllSources && sourceValue.length === 0;

  const chartData = useMemo(() => (
    bySource
      ? buildSourceCurvePoints(visibleMonths, sourceValue, sourceKey, applyFees, roasDays)
      : buildMonthCurvePoints(visibleMonths, selectedSources, applyFees, roasDays)
  ), [bySource, visibleMonths, sourceValue, sourceKey, selectedSources, applyFees, roasDays]);

  const lines = bySource
    ? sourceValue.map(source => ({ key: sourceKey[source], name: source, color: sourceColor[source] }))
    : visibleMonths.map(row => ({ key: row.name, name: formatMonth(row.name), color: monthColor[row.name] }));

  if (months.length === 0) {
    return (
      <p className="text-sm text-muted-foreground px-4 py-3">
        Нет полных месяцев для графика.
      </p>
    );
  }

  return (
    <div className="p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-muted-foreground mr-1">Источники:</span>
        <button
          type="button"
          onClick={() => onSetSources(isEverySelected ? [] : null)}
          className={`rounded-md border px-2 py-0.5 text-xs transition-colors ${isEverySelected ? 'bg-accent text-accent-foreground border-transparent' : 'hover:bg-muted'}`}
        >
          Все
        </button>
        <ToggleGroup
          type="multiple"
          size="sm"
          className="flex-wrap justify-start gap-1"
          value={sourceValue}
          onValueChange={(next) => onSetSources(next.length === allSources.length ? null : next)}
        >
          {allSources.map(source => (
            <ToggleGroupItem key={source} value={source} className="h-7 px-2 text-xs gap-1">
              {bySource && (
                <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: sourceColor[source] }} />
              )}
              <CampaignIcons source={source} size={14} />
              {source}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-muted-foreground mr-1">Месяцы:</span>
        <button
          type="button"
          onClick={() => onSetHiddenMonths([])}
          className="rounded-md border px-2 py-0.5 text-xs hover:bg-muted transition-colors"
        >
          Все
        </button>
        <button
          type="button"
          onClick={() => onSetHiddenMonths(months.map(row => row.name))}
          className="rounded-md border px-2 py-0.5 text-xs hover:bg-muted transition-colors"
        >
          Никого
        </button>
        {months.map(row => {
          const isOn = !hiddenMonths.includes(row.name);
          return (
            <button
              key={row.name}
              type="button"
              onClick={() => onToggleMonth(row.name)}
              className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs transition-colors ${isOn ? 'bg-accent text-accent-foreground border-transparent' : 'text-muted-foreground hover:bg-muted'}`}
            >
              {!bySource && (
                <span
                  className="inline-block h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: monthColor[row.name], opacity: isOn ? 1 : 0.35 }}
                />
              )}
              {formatMonth(row.name)}
            </button>
          );
        })}
      </div>

      {bySource && (
        <p className="text-xs text-muted-foreground">
          Линия — источник, сумма отмеченных месяцев. В точке D учитываются только месяцы, которым уже исполнилось D дней.
        </p>
      )}

      {hasNoSources ? (
        <p className="text-sm text-muted-foreground py-3">
          Выберите хотя бы один источник или нажмите «Все».
        </p>
      ) : visibleMonths.length === 0 ? (
        <p className="text-sm text-muted-foreground py-3">
          Включите хотя бы один месяц, чтобы увидеть кривые.
        </p>
      ) : (
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis
              dataKey="day"
              type="category"
              tickFormatter={(value) => `D${value}`}
              className="text-xs"
              tick={{ fill: 'currentColor' }}
              label={{ value: 'День от установки (ключевые дни, шаг неравномерный)', position: 'insideBottom', offset: -2 }}
            />
            <YAxis
              className="text-xs"
              tick={{ fill: 'currentColor' }}
              tickFormatter={(value) => `${value}%`}
              label={{ value: 'ROAS (%)', angle: -90, position: 'insideLeft' }}
            />
            <Tooltip content={<CurvesTooltip />} />
            <ReferenceLine y={100} stroke="#22c55e" strokeDasharray="5 5" ifOverflow="extendDomain" />
            {lines.map(line => (
              <Line
                key={line.key}
                type="monotone"
                dataKey={line.key}
                name={line.name}
                stroke={line.color}
                strokeWidth={2}
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
                connectNulls={false}
                isAnimationActive={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

/**
 * Сводка по всем месяцам
 */
function MonthlySummary({ data, grandTotal, applyFees, roasDays }) {
  // Считаем окупившиеся месяцы по лучшему доступному ROAS
  const profitableMonths = data.filter(m => (bestAvailableRoas(m, applyFees, roasDays) ?? 0) >= 100).length;
  const totalMonths = data.length;
  
  // Рассчитываем доходы с учетом галочки
  const revenue = calculateRevenue(grandTotal, applyFees);
  
  // Общий ROAS = накопленный на сегодня доход всех месяцев / затраты всех
  // месяцев — та же семантика, что у колонки Current ROAS. Идущий месяц входит
  // в обе части дроби: сервер отдаёт его реальный доход, а не null.
  const costsForRoas = grandTotal.costs || 0;
  const globalRoas = costsForRoas > 0 ? (revenue.total / costsForRoas) * 100 : 0;
  const roasColorClass = globalRoas >= 100 ? 'text-green-600' : globalRoas >= 50 ? 'text-yellow-600' : 'text-red-600';
  
  return (
    <div className="grid grid-cols-2 md:grid-cols-7 gap-4 p-4 bg-muted/50 rounded-lg mb-4">
      <div>
        <p className="text-xs text-muted-foreground">Всего месяцев</p>
        <p className="text-lg font-bold">{totalMonths}</p>
      </div>
      <div>
        <p className="text-xs text-muted-foreground">Окупившихся</p>
        <p className="text-lg font-bold text-green-600">{profitableMonths}</p>
      </div>
      <div>
        <p className="text-xs text-muted-foreground">Общие затраты</p>
        <p className="text-lg font-bold">{MetricsService.formatCurrency(grandTotal.costs)}</p>
      </div>
      <div>
        <p className="text-xs text-muted-foreground">Общий доход {applyFees ? '(чистый)' : '(полный)'}</p>
        <p className="text-lg font-bold">{MetricsService.formatCurrency(revenue.total)}</p>
        <p className={`text-xs font-semibold ${roasColorClass}`}>
          ROAS: {globalRoas.toFixed(1)}%
        </p>
      </div>
      <div>
        <p className="text-xs text-muted-foreground">InApps {applyFees ? '(NET)' : '(gross)'}</p>
        <p className={`text-lg font-bold ${INAPPS_COLOR}`}>{MetricsService.formatCurrency(revenue.inapps)}</p>
      </div>
      <div>
        <p className="text-xs text-muted-foreground">AdMon</p>
        <p className={`text-lg font-bold ${ADMON_COLOR}`}>{MetricsService.formatCurrency(revenue.admon)}</p>
      </div>
      <div>
        <p className="text-xs text-muted-foreground">Установки</p>
        <p className="text-lg font-bold">{MetricsService.formatNumber(grandTotal.installs)}</p>
        <p className="text-xs text-muted-foreground">
          CPI: {MetricsService.formatCurrency(grandTotal.cpi)}
        </p>
      </div>
    </div>
  );
}

function RoasMonthlySummaryWidget() {
  const [expanded, setExpanded] = useState({});
  
  // Читаем состояние галочки из localStorage
  // ВАЖНО: ключ 'applyPlatformFees' - такой же как в Sidebar.js
  const [applyFees, setApplyFees] = useState(() => {
    const stored = localStorage.getItem('applyPlatformFees');
    return stored === 'true';
  });
  
  // Слушаем изменения в localStorage (когда галочка меняется в Sidebar)
  useEffect(() => {
    const handleStorageChange = () => {
      const stored = localStorage.getItem('applyPlatformFees');
      setApplyFees(stored === 'true');
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('platformFeesChanged', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('platformFeesChanged', handleStorageChange);
    };
  }, []);

  const { includeOrganic } = useDashboard();

  // Дни ROAS, отмеченные галочками в заголовке таблицы, — идут на график по месяцам
  const [storedChartDays, setStoredChartDays] = useLocalStorage(CHART_DAYS_STORAGE_KEY, []);
  const toggleChartDay = (day, checked) => {
    const current = Array.isArray(storedChartDays) ? storedChartDays : [];
    const next = checked
      ? (current.includes(day) ? current : [...current, day])
      : current.filter(d => d !== day);
    setStoredChartDays(next);
  };

  const [storedChartMode, setChartMode] = useLocalStorage(CHART_MODE_STORAGE_KEY, 'days');
  const chartMode = storedChartMode === 'curves' ? 'curves' : 'days';
  const [storedHiddenMonths, setStoredHiddenMonths] = useLocalStorage(CURVES_HIDDEN_MONTHS_KEY, []);
  const hiddenMonths = Array.isArray(storedHiddenMonths) ? storedHiddenMonths : [];
  const toggleCurveMonth = (month) => {
    setStoredHiddenMonths(hiddenMonths.includes(month)
      ? hiddenMonths.filter(m => m !== month)
      : [...hiddenMonths, month]);
  };
  const [storedCurveSources, setStoredCurveSources] = useLocalStorage(CURVES_SOURCES_KEY, null);
  const curveSources = Array.isArray(storedCurveSources) ? storedCurveSources : null;
  const [storedCurvesBySource, setCurvesBySource] = useLocalStorage(CURVES_BY_SOURCE_KEY, false);
  const curvesBySource = storedCurvesBySource === true;

  const [sort, setSort] = useState(DEFAULT_SORT);
  const handleSort = (key) => setSort(prev => nextSort(prev, key));

  const widgetData = useWidgetData(async (filters, options) => {
    return MetricsService.getRoasMonthlySummary(filters, includeOrganic, options);
  }, [includeOrganic]);

  const widgetProps = useBaseWidget(widgetData);
  const { data: rawData } = widgetProps;

  // Дальние дни (D450, D540, …) — те, что посчитал сервер в этом ответе
  const roasDays = useMemo(() => getRoasDays(rawData), [rawData]);

  const selectedChartDays = useMemo(
    () => roasDays.filter(day => Array.isArray(storedChartDays) && storedChartDays.includes(day)),
    [roasDays, storedChartDays]
  );

  const processedData = useMemo(() => {
    if (!rawData || !Array.isArray(rawData)) {
      return { data: [], grandTotal: {} };
    }

    const data = rawData.filter(row => row?.name && row.name >= MIN_MONTH);

    // Рассчитываем grandTotal с раздельными доходами по платформам
    const grandTotal = {
      costs: data.reduce((sum, row) => sum + (row.costs || 0), 0),
      installs: data.reduce((sum, row) => sum + (row.installs || 0), 0),
      revenue_inapps_android: data.reduce((sum, row) => sum + (row.revenue_inapps_android || 0), 0),
      revenue_inapps_ios: data.reduce((sum, row) => sum + (row.revenue_inapps_ios || 0), 0),
      revenue_admon_android: data.reduce((sum, row) => sum + (row.revenue_admon_android || 0), 0),
      revenue_admon_ios: data.reduce((sum, row) => sum + (row.revenue_admon_ios || 0), 0),
      ...sumNetFields(data, ['revenue_inapps_android_net', 'revenue_inapps_ios_net']),
    };

    // Агрегируем раздельные доходы по дням — и, отдельно, costs ТОЛЬКО по
    // месяцам, где этот день уже зрел (иначе Grand Total ROAS для дальних дней
    // делится на costs незрелых месяцев, у которых числитель ещё не накопился,
    // — асимметрия числитель/знаменатель)
    roasDays.forEach(day => {
      const isMatureRow = (row) => isDayMature(row, day);

      grandTotal[`revenue_day_${day}_inapps_android`] = data.reduce((sum, row) => sum + (row[`revenue_day_${day}_inapps_android`] || 0), 0);
      grandTotal[`revenue_day_${day}_inapps_ios`] = data.reduce((sum, row) => sum + (row[`revenue_day_${day}_inapps_ios`] || 0), 0);
      grandTotal[`revenue_day_${day}_admon_android`] = data.reduce((sum, row) => sum + (row[`revenue_day_${day}_admon_android`] || 0), 0);
      grandTotal[`revenue_day_${day}_admon_ios`] = data.reduce((sum, row) => sum + (row[`revenue_day_${day}_admon_ios`] || 0), 0);
      Object.assign(grandTotal, sumNetFields(data, [
        `revenue_day_${day}_inapps_android_net`,
        `revenue_day_${day}_inapps_ios_net`
      ]));
      grandTotal[`costs_for_day_${day}`] = data.reduce((sum, row) => sum + (isMatureRow(row) ? (row.costs || 0) : 0), 0);
    });

    grandTotal.cpi = grandTotal.installs > 0 ? grandTotal.costs / grandTotal.installs : 0;

    return { data, grandTotal };
  }, [rawData, roasDays]);

  // Месяцы и платформы стоят на месте, сортировка по колонкам двигает только источники
  const sortedMonths = useMemo(
    () => sortByName(processedData.data, 'desc'),
    [processedData.data]
  );

  const orderChildren = (row) => (row.platforms
    ? sortByName(row.platforms)
    : sortRows(row.sources || [], sort, applyFees));

  const toggleExpand = (key) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  const renderRow = (row, level = 0, keyPrefix = '') => {
    if (!row || !row.name) return null;
    const { name, platforms, sources, ...metrics } = row;
    const isExpanded = expanded[keyPrefix];
    const hasChildren = (platforms && platforms.length > 0) || (sources && sources.length > 0);

    // Рассчитываем доходы с учетом галочки
    const revenue = calculateRevenue(metrics, applyFees);
    const totalRevenue = pickTotalRevenue(metrics, applyFees);

    const rowStyles = {
      0: 'bg-muted/50 hover:bg-muted/70 font-semibold',
      1: 'bg-background hover:bg-muted/30',
      2: 'bg-muted/20 hover:bg-muted/40 text-sm'
    };

    return (
      <React.Fragment key={keyPrefix}>
        <TableRow className={rowStyles[level] || ''}>
          <TableCell style={{ paddingLeft: `${level * 24 + 12}px` }}>
            <div className="flex items-center gap-2">
              {hasChildren && (
                <button 
                  onClick={() => toggleExpand(keyPrefix)} 
                  className="p-1 rounded hover:bg-muted-foreground/20 transition-colors"
                >
                  {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </button>
              )}
              {!hasChildren && level > 0 && <div className="w-6" />}
              {level === 1 && <CampaignIcons platform={name} size={18} />}
              {level === 2 && <CampaignIcons source={name} size={16} />}
              <span className={level === 0 ? 'font-semibold' : ''}>
                {level === 0 ? formatMonth(name) : name}
              </span>
              {level === 0 && <MonthStatus metrics={metrics} applyFees={applyFees} roasDays={roasDays} />}
            </div>
          </TableCell>
          <TableCell className="text-right font-medium">
            {MetricsService.formatCurrency(metrics.costs)}
          </TableCell>
          <TableCell className="text-right">
            {MetricsService.formatNumber(metrics.installs)}
          </TableCell>
          <TableCell className="text-right text-muted-foreground">
            {metrics.installs > 0 && metrics.costs > 0 
              ? MetricsService.formatCurrency(metrics.costs / metrics.installs) 
              : '—'}
          </TableCell>
          <TableCell className={`text-right ${INAPPS_COLOR}`}>
            {revenue.hasData ? MetricsService.formatCurrency(revenue.inapps) : '—'}
          </TableCell>
          <TableCell className={`text-right ${ADMON_COLOR}`}>
            {revenue.hasData ? MetricsService.formatCurrency(revenue.admon) : '—'}
          </TableCell>
          <TableCell className="text-right font-medium">
            {totalRevenue != null ? MetricsService.formatCurrency(totalRevenue) : '—'}
          </TableCell>
          <RoasValueCell roas={pickCurrentRoas(metrics, applyFees)} />
          {roasDays.map(day => (
            <RoasCell key={day} metrics={metrics} day={day} applyFees={applyFees} />
          ))}
        </TableRow>
        {isExpanded && orderChildren(row).map(child =>
          renderRow(child, level + 1, `${keyPrefix}-${child.name}`)
        )}
      </React.Fragment>
    );
  };

  // Рассчитываем доходы для grandTotal с учетом галочки
  const grandTotalRevenue = calculateRevenue(processedData.grandTotal, applyFees);
  // Grand Total — сумма неокруглённых серверных значений по месяцам, делённая
  // на затраты всех месяцев (семантика Current ROAS: доход на сегодня / costs)
  const grandTotalCosts = processedData.grandTotal.costs || 0;
  const grandTotalCurrentRoas = grandTotalCosts > 0
    ? (grandTotalRevenue.total / grandTotalCosts) * 100
    : null;

  return (
    <BaseWidget 
      {...widgetProps} 
      title="ROAS по месяцам" 
      subtitle={`Сводная таблица по всем кампаниям и платформам ${applyFees ? '(чистый доход)' : '(полный доход)'}`}
      emptyMessage="Нет данных для отображения" 
      skeletonRows={5}
    >
      <div className="space-y-4">
        {processedData.data.length > 0 && (
          <MonthlySummary data={processedData.data} grandTotal={processedData.grandTotal} applyFees={applyFees} roasDays={roasDays} />
        )}
        
        {processedData.data.length > 0 && (
          <Card>
            <CardContent className="p-0">
              <div className="flex items-center justify-between gap-2 px-4 pt-3">
                <p className="text-sm font-medium">
                  {chartMode !== 'curves'
                    ? 'ROAS выбранных дней по месяцам'
                    : curvesBySource ? 'Кривые окупаемости по источникам' : 'Кривые окупаемости по месяцам'}
                </p>
                <div className="flex flex-wrap items-center justify-end gap-2">
                  {chartMode === 'curves' && (
                    <button
                      type="button"
                      aria-pressed={curvesBySource}
                      onClick={() => setCurvesBySource(!curvesBySource)}
                      className={`h-7 rounded-md border px-2 text-xs transition-colors ${curvesBySource ? 'bg-accent text-accent-foreground border-transparent' : 'hover:bg-muted'}`}
                    >
                      Разбить по источникам
                    </button>
                  )}
                  <ToggleGroup type="single" size="sm" value={chartMode} onValueChange={(value) => value && setChartMode(value)}>
                    <ToggleGroupItem value="days" className="h-7 px-2 text-xs">По дням</ToggleGroupItem>
                    <ToggleGroupItem value="curves" className="h-7 px-2 text-xs">Кривые окупаемости</ToggleGroupItem>
                  </ToggleGroup>
                </div>
              </div>
              {chartMode === 'curves' ? (
                <MonthlyCurvesChart
                  data={processedData.data}
                  roasDays={roasDays}
                  applyFees={applyFees}
                  hiddenMonths={hiddenMonths}
                  onToggleMonth={toggleCurveMonth}
                  onSetHiddenMonths={setStoredHiddenMonths}
                  selectedSources={curveSources}
                  onSetSources={setStoredCurveSources}
                  bySource={curvesBySource}
                />
              ) : (
                <MonthlyRoasChart
                  data={processedData.data}
                  selectedDays={selectedChartDays}
                  applyFees={applyFees}
                />
              )}
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent className="p-0">
            <div className="overflow-auto max-h-[70vh] [&>[data-slot=table-container]]:overflow-visible">
              <Table>
                <TableHeader className="sticky top-0 z-10 [&_th]:bg-muted [&_th]:shadow-[inset_0_-1px_0_hsl(var(--border))]">
                  <TableRow>
                    <SortableHead sortKey="name" sort={sort} onSort={handleSort} className="min-w-[200px]">Месяц / Платформа / Источник</SortableHead>
                    <SortableHead sortKey="costs" sort={sort} onSort={handleSort} className="text-right">Costs</SortableHead>
                    <SortableHead sortKey="installs" sort={sort} onSort={handleSort} className="text-right">Installs</SortableHead>
                    <SortableHead sortKey="cpi" sort={sort} onSort={handleSort} className="text-right">CPI</SortableHead>
                    <SortableHead sortKey="inapps" sort={sort} onSort={handleSort} className={`text-right ${INAPPS_COLOR}`}>InApps {applyFees ? '(NET)' : ''}</SortableHead>
                    <SortableHead sortKey="admon" sort={sort} onSort={handleSort} className={`text-right ${ADMON_COLOR}`}>AdMon</SortableHead>
                    <SortableHead sortKey="total" sort={sort} onSort={handleSort} className="text-right whitespace-nowrap">Total {applyFees ? '(net)' : ''}</SortableHead>
                    <SortableHead sortKey="current_roas" sort={sort} onSort={handleSort} className="text-right whitespace-nowrap">Current ROAS</SortableHead>
                    {roasDays.map(day => (
                      <TableHead key={day} className="text-right whitespace-nowrap text-xs" aria-sort={ariaSort(sort, `D${day}`)}>
                        <div className="inline-flex items-center gap-0.5">
                          {/* Подпись D-дня теперь сортирует, поэтому зона нажатия галочки расширена отдельно */}
                          <label
                            htmlFor={`roas-chart-day-${day}`}
                            className="inline-flex items-center p-1.5 -m-0.5 rounded cursor-pointer hover:bg-muted"
                            title="Показать на графике"
                          >
                            <Checkbox
                              id={`roas-chart-day-${day}`}
                              className="h-3 w-3"
                              aria-label={`Показать D${day} на графике`}
                              checked={selectedChartDays.includes(day)}
                              onCheckedChange={(checked) => toggleChartDay(day, checked === true)}
                            />
                          </label>
                          <SortButton sortKey={`D${day}`} sort={sort} onSort={handleSort}>D{day}</SortButton>
                        </div>
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {/* Grand Total закреплён сразу под шапкой (на 1px внахлёст с шапкой h-10, чтобы не было щели) и не участвует в сортировке */}
                  <TableRow className="bg-muted font-bold [&>td]:sticky [&>td]:top-[calc(2.5rem-1px)] [&>td]:z-[5] [&>td]:bg-muted [&>td]:shadow-[inset_0_-1px_0_hsl(var(--border))]">
                    <TableCell>Grand Total</TableCell>
                    <TableCell className="text-right">{MetricsService.formatCurrency(processedData.grandTotal.costs)}</TableCell>
                    <TableCell className="text-right">{MetricsService.formatNumber(processedData.grandTotal.installs)}</TableCell>
                    <TableCell className="text-right">{MetricsService.formatCurrency(processedData.grandTotal.cpi)}</TableCell>
                    <TableCell className={`text-right ${INAPPS_COLOR}`}>{MetricsService.formatCurrency(grandTotalRevenue.inapps)}</TableCell>
                    <TableCell className={`text-right ${ADMON_COLOR}`}>{MetricsService.formatCurrency(grandTotalRevenue.admon)}</TableCell>
                    <TableCell className="text-right">{MetricsService.formatCurrency(grandTotalRevenue.total)}</TableCell>
                    <RoasValueCell roas={grandTotalCurrentRoas} />
                    {roasDays.map(day => (
                      <RoasCell
                        key={day}
                        metrics={{ ...processedData.grandTotal, costs: processedData.grandTotal[`costs_for_day_${day}`] }}
                        day={day}
                        applyFees={applyFees}
                      />
                    ))}
                  </TableRow>
                  {sortedMonths.map(row => renderRow(row, 0, `month-${row.name}`))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </BaseWidget>
  );
}

export { RoasMonthlySummaryWidget };
export default RoasMonthlySummaryWidget;
